/**
 * FPC-534 — Phase 2: canonical FE accessor for `window.__APP__.SESSION`.
 *
 * Reads from the BFF-injected `__APP__.SESSION` namespace when the
 * `fpc-534-session-bootstrap` feature flag is on, falling back to the
 * legacy PHP-injected globals (`window.DP_LOCALE_INFO`, `window.$dt.*`)
 * when it is off.
 *
 * Consumers should depend on these accessors instead of reading the legacy
 * globals directly. Once every legacy-global consumer has migrated, the
 * fallback branch and the PHP injection of that global can be deleted.
 */
import { getAppInstance, getWindow } from '@/lib/app';
import type {
  WebappAppSession,
  WebappAppSessionLocaleInfo,
  WebappAppSessionObjConfig,
  WebappAppSessionObjLoginUser,
  WebappAppSessionObjMyInstallInfo,
} from '@/types/session';

const LOCALE_DEFAULTS: WebappAppSessionLocaleInfo = {
  decimal_point: '.',
  thousands_sep: ',',
  int_curr_symbol: 'AUD ',
  currency_symbol: '$',
  mon_decimal_point: '.',
  mon_thousands_sep: ',',
  positive_sign: '',
  negative_sign: '-',
  int_frac_digits: 2,
  frac_digits: 2,
  p_cs_precedes: 1,
  p_sep_by_space: 0,
  n_cs_precedes: 1,
  n_sep_by_space: 0,
  p_sign_posn: 1,
  n_sign_posn: 1,
  grouping: [3],
  mon_grouping: [3],
};

/**
 * Returns the consolidated session object if the BFF path populated it,
 * otherwise `undefined`. Callers that have safe legacy fallbacks should
 * branch on this; callers that don't should use the narrow accessors below.
 */
export function getSession(): WebappAppSession | undefined {
  return getAppInstance().SESSION;
}

export function getLocaleInfo(): WebappAppSessionLocaleInfo {
  const session = getSession();
  if (session?.DP_LOCALE_INFO) {
    return session.DP_LOCALE_INFO;
  }
  const legacy = (getWindow() as unknown as { DP_LOCALE_INFO?: WebappAppSessionLocaleInfo })
    .DP_LOCALE_INFO;
  return legacy ?? LOCALE_DEFAULTS;
}

export function getObjConfig(): WebappAppSessionObjConfig | undefined {
  const session = getSession();
  if (session?.objConfig) {
    return session.objConfig;
  }
  return (getWindow() as { $dt?: { objConfig?: WebappAppSessionObjConfig } }).$dt?.objConfig;
}

export function getObjLoginUser(): WebappAppSessionObjLoginUser | undefined {
  const session = getSession();
  if (session?.objLoginUser) {
    return session.objLoginUser;
  }
  return (getWindow() as { $dt?: { objLoginUser?: WebappAppSessionObjLoginUser } }).$dt
    ?.objLoginUser;
}

export function getObjMyInstallInfo(): WebappAppSessionObjMyInstallInfo | undefined {
  const session = getSession();
  if (session?.objMyInstallInfo) {
    return session.objMyInstallInfo;
  }
  return (getWindow() as { $dt?: { objMyInstallInfo?: WebappAppSessionObjMyInstallInfo } }).$dt
    ?.objMyInstallInfo;
}
